<template>
  <form @submit.prevent novalidate="true">
    <b-card>
      <template slot="header">
        <div class="form-group form-row mb-0">
          <div class="col-lg-6">
            <input class="form-control" type="text" readonly :value="generalSettings ? `${generalSettings.Database} (v${generalSettings.DatabaseVersion})` : ''" />
          </div>
          <div class="col-1 ml-auto" v-bind:class="{ inlineSpinner: isBusy }"></div>
        </div>
      </template>

      <div v-if="generalSettings">
        <div class="form-group form-row">
          <label for="Environment" class="col-lg-4 col-md-6 col-form-label pt-0">Environment</label>
          <div class="col">
            <b-form-radio-group id="Environment"
                                buttons button-variant="outline-secondary" size="sm"
                                v-model="generalSettings.Environment"
                                :disabled="isBusy"
                                v-validate="'required'"
                                data-vv-name="Environment">
              <b-form-radio :value="'Development'">Development</b-form-radio>
              <b-form-radio :value="'Staging'">Staging</b-form-radio>
              <b-form-radio :value="'Production'">Production</b-form-radio>
              <b-form-radio :value="'Demo'">Demo</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="form-group form-row">
          <label for="LoopVersion" class="col-lg-4 col-md-6 col-form-label pt-0">LoopVersion</label>
          <div class="col">
            <b-form-radio-group id="LoopVersion"
                                buttons button-variant="outline-secondary" size="sm"
                                v-model="generalSettings.LoopVersion"
                                :disabled="isBusy"
                                v-validate="'required'"
                                data-vv-name="LoopVersion">
              <b-form-radio :value="'v1'">v1</b-form-radio>
              <b-form-radio :value="'v2'">v2</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <div class="form-group form-row">
          <label for="WebportalUrl" class="col-lg-4 col-md-6 col-form-label pt-0">WebportalUrl</label>
          <div class="col">
            <b-form-input id="WebportalUrl"
                          v-model="generalSettings.WebportalUrl"
                          size="sm"
                          :disabled="isBusy"
                          v-validate="'required'"
                          data-vv-name="WebportalUrl">
            </b-form-input>
          </div>
          <div class="col-auto">
            <b-button variant="secondary" size="sm" @click="setWebportalUrl()">
              <font-awesome-icon :icon="['fal', 'window']" /> {{ $t('Webportal.Apply') }}
            </b-button>
          </div>
        </div>

        <div class="form-group form-row">
          <label for="PlatformCode" class="col-lg-4 col-md-6 col-form-label pt-0">PlatformCode</label>
          <div class="col-lg-4 col-md-6">
            <b-form-select id="PlatformCode"
              v-model="generalSettings.PlatformCode"
              v-validate data-vv-name="PlatformCode"
              size="sm"
              :disabled="isBusy">
              <b-form-select-option 
                v-for="platform in generalSettings.Platforms"
                :key="platform.Code"
                :value="platform.Code">
                {{ platform.Code }} - {{ platform.Name }}
              </b-form-select-option>
            </b-form-select>
          </div>
        </div>

        <hr />

        <div class="form-group form-row">
          <label for="CreateDefaultNotificationForExternalUsers" class="col-lg-4 col-md-6 col-form-label pt-0">
            {{ $t('Webportal.Administration.CreateDefaultNotificationForExternalUsers') }}
            <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.CreateDefaultNotificationForExternalUsers.Description')">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
          </label>
          <div class="col">
            <label class="switch switch-label switch-secondary">
              <input class="switch-input" type="checkbox"
                     v-model="generalSettings.CreateDefaultNotificationForExternalUsers"
                     v-validate data-vv-name="CreateDefaultNotificationForExternalUsers"
                     :disabled="isBusy">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
            </label>
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="float-right">
          <button type="button" @click="cancel" class="btn btn-secondary" :disabled="!isFormDirty || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t('Webportal.Cancel') }}
          </button>
          <button type="button" @click="submit" class="btn btn-primary" :disabled="!isFormDirty || !isFormValid || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t('Webportal.Save') }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import AdministationService from '@/services/AdministationService';

export default {
  name: "GeneralSettings",

  mixins: [ AppConfig ],

  data () {
    return {
      generalSettings: null,

      isBusy: false
    }
  },

  mounted() {
    this.initializeTheme();
  },

  computed: {
    isFormDirty() {
      return Object.keys(this.validationFields).some(key => this.validationFields[key].dirty);
    },
    isFormValid() {
      return Object.keys(this.validationFields).every(key => this.validationFields[key].valid === null || this.validationFields[key].valid);
    }
  },

  activated() {
    this.getGeneralSettings();
  },

  beforeRouteLeave (to, from, next) {
    this.$emit('unsavedChanges', this.isFormDirty, next);
  },

  methods: {
    getGeneralSettings() {
      AdministationService.getGeneralSettings()
        .then(response => {
          this.isBusy = false;

          this.generalSettings = response.data ? response.data[0] : null;
          this.$validator.reset();
        });
    },

    setWebportalUrl() {
      this.generalSettings.WebportalUrl = window.location.origin;
      this.validationFields['WebportalUrl'].dirty = true;
    },

    cancel() {
      this.$emit('unsavedChanges', this.isFormDirty, result => {
        if (result !== false) this.getGeneralSettings();
      });
    },

    submit() {
      this.isBusy = true;

      AdministationService.setGeneralSettings(this.generalSettings)
        .then(response => {
          this.isBusy = false;
          this.$validator.reset();
        })
        .catch(_ => { this.isBusy = false; } );
    }
  }
}
</script>
